import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { WP_REST_API_Post } from 'wp-types';
import { WP_Page } from '../../model/models';
import { ModelsService } from '../../services/ModelsService';
import { Faq } from '../faq/Faq';
import Form from '../forms/Forms';
import GForm from '../gforms/GForm';
import { Error404 } from '../globals/Error404';
import HTMLContent from '../globals/HTMLContent';
import { SanitizeHTML } from '../globals/SanitizeHTML';

interface PageContentProps {
  baseLink: string;
  pages: WP_Page[];
  slugs: string[];
}

export const PageContent: React.FC<PageContentProps> = ({
  pages,
  slugs,
  baseLink,
}) => {
  return slugs.length === 0 ? (
    <PageMenu pages={pages} />
  ) : (
    <Page baseLink={baseLink} pages={pages} slugs={slugs} />
  );
};

interface PageMenuProps {
  pages: WP_REST_API_Post[];
}
const PageMenu: React.FC<PageMenuProps> = ({ pages }) => <Error404 />;

const Page: React.FC<PageContentProps> = ({ baseLink, pages, slugs }) => {
  const currentSlug = '/' + slugs.join('/');

  if (pages.length === 0) {
    // loading...
    return <></>;
  }

  const page = pages.find(
    (s) => ModelsService.getPageFullSlug(s) === currentSlug
  );

  if (!page) {
    // page not found...
    return <Error404 />;
  }

  return (
    <>
      {slugs.length > 1 && (
        <PageBreadcrumb
          baseBreadcrumbLabel="Pages"
          baseLink={baseLink}
          pages={pages}
          page={page}
        />
      )}

      <SanitizeHTML
        tag="h1"
        html={page.title.rendered}
        className="mt-6 text-black font-medium text-2xl mb-8 pb-1 text-center"
      />

      <div className="styled-content">
        <HTMLContent html={page.content.rendered} />
        <Faq faq={page.acf.faq} page={true} />
        <Form postId={page.id} form={page.acf.form} />
        {page.acf.gform?.gform_activation === true && page.acf.gform.gform && (
          <GForm form={page.acf.gform.gform} />
        )}
      </div>
    </>
  );
};

interface PageBreadcrumbProps {
  baseLink: string;
  baseBreadcrumbLabel: string;
  page: WP_REST_API_Post;
  pages: WP_REST_API_Post[];
}
interface BreadcrumbItem {
  label: string;
  link: string;
}
const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  baseLink,
  baseBreadcrumbLabel,
  page,
  pages,
}) => {
  const intl = useIntl();

  const servicesPath: BreadcrumbItem[] = ModelsService.getAncestors(page, pages)
    .reverse()
    .map((s) => ({
      label: s.title.rendered,
      link: baseLink + ModelsService.getPageFullSlug(s),
    }));
  const items = [
    { label: intl.formatMessage({ id: baseBreadcrumbLabel }), link: baseLink },
  ].concat(servicesPath);

  // index of last item
  const last = items.length - 1;

  const Separator = () => <span className="mx-2">&gt;</span>;

  return (
    <ul className="flex text-gray-500 text-2xs lg:text-base px-2">
      {items.map((item, idx) => {
        const isLast = idx === last;
        return (
          <li
            key={idx}
            className={
              'inline-flex items-center ' + (isLast ? '' : 'text-primary')
            }
          >
            <Link to={item.link}>
              <SanitizeHTML html={item.label} />
            </Link>
            {!isLast && <Separator />}
          </li>
        );
      })}
    </ul>
  );
};
